import React, { useState } from 'react'
import CardList from '../card-list-v2'
import { slugify } from '../../helpers'
import { SEE_MORE_MOVIES_TEXT, MIN_MOVIES_TO_SHOW_V2 } from '../../constants'
import { H3 } from '@hub/heading'
import { Box } from '@hub/box'
import { Button } from '@hub/button'
import { Text } from '@hub/text'
import { CoreContainer } from '@hub/core-container'
import { useToken } from '@hub/design-system-base'
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@hub/tabs'
// eslint-disable-next-line no-restricted-syntax
import { DEFAULT_BREAKPOINT, useBreakpointValue } from '@hub/design-system-base'

export type MovieListItem = {
  id: number | string
  title: string
  image: string
  genre: string
  classification: string
  sessions: string[]
}

interface TabHeading {
  displayDate: string
  date: string
}

interface NowShowingProps {
  entries: MovieListItem[]
  centreSlug: string
  tabHeadings: TabHeading[]
  title: string
}

interface MovieCardProps extends MovieListItem {
  link: string
}

const toMovieCard = (
  movie: MovieListItem,
  centreId: string,
  date: string
): MovieCardProps => ({
  ...movie,
  link: `/${centreId}/movie/${movie.id}/${slugify(movie.title)}${
    date && date !== 'All' ? `/${date}` : ''
  }`,
})

const getCardsForList = (
  activeTabDate: string,
  entries: MovieListItem[],
  centreSlug: string
): MovieCardProps[] =>
  entries
    .filter(
      movie => activeTabDate === 'All' || movie.sessions.includes(activeTabDate)
    )
    .map(movie => toMovieCard(movie, centreSlug, activeTabDate))

const MovieTab: React.FC<{ heading: string }> = ({ heading }) => {
  const borderPrimary = useToken('colors', 'borderPrimary')
  const borderSecondary = useToken('colors', 'borderSecondary')

  return (
    <Tab
      _active={{ borderColor: borderPrimary }}
      sx={{
        height: 'size-8',
        paddingX: 'spacing-lg',
        borderColor: borderSecondary,
      }}
    >
      {heading}
    </Tab>
  )
}

const TabContents: React.FC<{
  cardsForList: MovieCardProps[]
  isAllTab: boolean
  selectAllTab: () => void
}> = ({ cardsForList, isAllTab, selectAllTab }) => {
  if (cardsForList.length > 0) {
    return (
      <Box sx={{ textAlign: 'left' }}>
        <CardList
          cards={cardsForList}
          buttonText={isAllTab ? SEE_MORE_MOVIES_TEXT : ''}
          minItems={isAllTab ? MIN_MOVIES_TO_SHOW_V2 : cardsForList.length}
        />
      </Box>
    )
  } else {
    const unsuccessfulText =
      "We haven't confirmed movies for that day yet. How about another day?"

    return (
      <Box sx={{ textAlign: 'center' }}>
        <Text sx={{ paddingTop: 'spacing-lg', paddingBottom: 'spacing-lg' }}>
          {unsuccessfulText}
        </Text>
        <Button
          sx={{
            textDecoration: 'underline',
            fontWeight: 'normal',
            textTransform: 'none',
            color: 'textPrimary',
            pointer: 'cursor',
          }}
          variant="link"
          onClick={selectAllTab}
        >
          {SEE_MORE_MOVIES_TEXT}
        </Button>
      </Box>
    )
  }
}

const firstWord = (sentence: string): string => sentence.split(' ')[0]

const truncateHeading = ({ date, displayDate }: TabHeading): TabHeading => ({
  date,
  displayDate: firstWord(displayDate),
})

const NowShowing: React.FC<NowShowingProps> = ({
  entries,
  centreSlug,
  tabHeadings,
  title,
}) => {
  const baseHeadings = tabHeadings.map(truncateHeading)
  const lgHeadings = tabHeadings
  const adjustedHeadings =
    useBreakpointValue(
      [baseHeadings, null, null, lgHeadings],
      DEFAULT_BREAKPOINT
    ) ?? baseHeadings

  const [activeTab, switchTabs] = useState(0)
  const allTab: number = tabHeadings
    .map(heading => heading.displayDate)
    .indexOf('All')

  const selectAllTab = (): void => switchTabs(allTab)

  return (
    <CoreContainer>
      <Tabs
        size="lg"
        align="center"
        index={activeTab}
        onChange={index => switchTabs(index)}
      >
        <Box>
          <Box sx={{ textAlign: 'center' }}>
            <H3>{title}</H3>
          </Box>
          <Box
            sx={{
              overflowX: 'auto',
              overflowY: 'clip',
              padding: 'spacing-xs',
            }}
          >
            <TabList
              sx={{
                borderStyle: 'none',
                width: 'max-content',
              }}
            >
              {adjustedHeadings.map(({ displayDate, date }) => (
                <MovieTab key={date} heading={displayDate}></MovieTab>
              ))}
            </TabList>
          </Box>
        </Box>
        <TabPanels>
          {adjustedHeadings.map(({ date }, tab) => {
            return (
              <TabPanel key={date}>
                <TabContents
                  selectAllTab={selectAllTab}
                  isAllTab={tab === allTab}
                  cardsForList={getCardsForList(date, entries, centreSlug)}
                />
              </TabPanel>
            )
          })}
        </TabPanels>
      </Tabs>
    </CoreContainer>
  )
}

export default NowShowing
